<template>
  <div v-if="!ready" class="mhr-hero-full d-flex align-center justify-center">
    <div class="mhr-text-h2 mhr-font mhr-blue--text">Loading</div>
  </div>
  <div v-else>
    <div id="mhr-hero" class="pos-relative d-flex flex-column align-center mhr-blue">
      <video
        class="mhr-hero-full"
        autoplay loop muted playsinline
        :poster="`${baseUrl}assets/image/poster.jpg`"
        style="object-fit: cover;"
      >
        <source
          :src="`${baseUrl}assets/video/megaworldhotel-bg-video.av1.mp4`"
          type="video/mp4; codecs=av01.0.08M.08,opus"
        >
        <source
          :src="`${baseUrl}assets/video/megaworldhotel-bg-video.h264.mp4`"
          type="video/mp4"
        >
      </video>
      <div class="pos-absolute d-flex flex-column fill-height mhr-white--text align-center justify-end mhr-hero-home">
        <v-spacer />
        <div class="mb-md-16 pb-md-4 d-flex flex-column align-center">
          <v-img
            v-if="dataConfig.corp_logo"
            contain
            class="mhr-hero-logo mb-9"
            :src="dataConfig.corp_logo"
          />
          <div v-if="dataPage.page_subtitle" class="text-center mhr-text-hero-locations">
            <template v-for="(city, c) in dataPage.page_subtitle.split(' ٠ ')">
              <span v-if="c % 2" :key="c" class="px-1">٠</span>
              <template v-else-if="c"><br v-if="isMobile" :key="c">
                <span v-else :key="-c" class="px-1">٠</span>
              </template>
              {{ city }}
            </template>
          </div>
        </div>
        <v-spacer class="d-md-none" />
        <img :src="`${baseUrl}assets/icon/scroll.svg`" class="mhr-hero-scrolldown" width="59" height="82">
      </div>
    </div>


    <v-container class="d-flex flex-column align-center">

      <div class="mhr-section d-flex flex-column align-center text-center">
        <div class="mhr-text-h1 mhr-font" v-html="dataLabels.HOMEPAGE_ABOUT_HEADLINE" />
        <v-form ref="form" v-model="booking.valid" class="mhr-book-form d-flex flex-column flex-lg-row align-center">
          <v-row no-gutters class="flex-grow-1">
            <v-col cols="12" lg="5" class="pe-lg-2">
              <v-autocomplete
                v-model="booking.hotel"
                dense single-line hide-details flat solo attach
                color="mhr-black"
                class="mhr-book-field rounded-0"
                append-icon=""
                autocomplete="nope"
                label="DESTINATION"
                :items="dataHotels.map( ({ hotel_name }, index) => ({ hotel_name, index }) )"
                item-text="hotel_name"
                item-value="index"
                :menu-props="{ rounded: 'lg' }"
                :rules="rules.required"
              >
                <v-icon slot="prepend-inner" color="mhr-blue" class="mx-1">fa-solid fa-location-dot</v-icon>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" lg="4" class="px-lg-2 pos-relative">
              <v-menu
                v-model="booking.periodModel"
                attach offset-y
                :close-on-content-click="false"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    dense readonly hide-details single-line flat solo attach
                    color="mhr-black"
                    label="PERIOD"
                    class="mhr-book-field rounded-0"
                    :value="booking.periodRange.length === 2 ? [...booking.periodRange].sort().map(d => formatDate(d)).join(' — ') : null"
                    :rules="rules.required"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon slot="prepend-inner" color="mhr-blue" class="mx-1">fa-solid fa-calendar-days</v-icon>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="booking.periodRange"
                  range reactive no-title full-width
                  :min="new Date().toISOString().substring(0, 10)"
                  color="mhr-blue"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" lg="3" class="px-lg-2">
              <v-text-field
                v-model="booking.promo"
                dense single-line hide-details flat solo attach
                color="mhr-black"
                label="PROMO CODE"
                class="mhr-book-field rounded-0"
              >
                <v-icon slot="prepend-inner" color="mhr-blue" class="mx-1">fa-solid fa-percent</v-icon>
              </v-text-field>
            </v-col>
          </v-row>
          <v-btn
            tile color="mhr-blue" elevation="0"
            class="ms-lg-6 mhr-book-btn mhr-white--text"
            :href="bookingLink"
            target="_blank" rel="noopener nofollow"
            @click="
              bookingLink ? gtagBookEvent('Home', dataHotels[booking.hotel].hotel_name) : $refs.form.validate()
            "
            v-html="dataLabels.TEXT_BOOKNOW"
          />
        </v-form>
        <div class="mhr-width-body" v-html="dataLabels.HOMEPAGE_ABOUT_DESCRIPTION" />
      </div>


      <v-divider v-show="!isMobile" class="mhr-divider mhr-width-body" />


      <div class="mhr-section">

        <div class="mhr-font mhr-text-h2 text-center" v-html="dataLabels.HOMEPAGE_BRAND_HEADLINE" />

        <div class="mhr-brands pos-relative d-flex flex-column">

          <div
            class="mhr-brands-row ps-lg-8"
            :class="!isMobile && 'pos-absolute d-flex flex-column'"
          >
            <swiper
              slide-to-clicked-slide
              :navigation="!isMobile"
              :slides-per-view="3.5"
              :space-between="24"
              :allow-touch-move="isMobile"
              class="pos-relative"
              @swiper="$set(swipers, 'brands', $event)"
            >
              <div
                v-if="swipers.brands"
                class="pos-absolute d-flex flex-column mhr-swiper-mask"
                :class="`
                  ${ swipers.brands.isBeginning && 'mhr-swiper-mask-beginning' }
                  ${ swipers.brands.isEnd && 'mhr-swiper-mask-end' }
                `"
              />
              <swiper-slide v-for="({ brand_name, brand_logo }, b) in dataBrands" :key="brand_name">
                <v-img
                  v-if="brand_logo"
                  contain
                  :class="selBrandIndex !== b && 'opacity-3 cursor-pointer'"
                  :src="brand_logo"
                  @click="selBrandIndex = b"
                />
                <div
                  v-else
                  class="mhr-text-h8 mhr-font d-flex align-center justify-center fill-height text-lowercase text-center white rounded-lg"
                  :class="selBrandIndex !== b && 'opacity-3 cursor-pointer'"
                  @click="selBrandIndex = b"
                  v-text="brand_name"
                />
              </swiper-slide>
            </swiper>
          </div>

          <div class="mhr-brands-img elevation-4 mhr-blue">
            <v-fade-transition mode="out-in" appear>
              <v-img
                v-for="({ brand_name, brand_thumbnail }) in [selBrand]" :key="brand_name"
                height="100%"
                :src="brand_thumbnail"
              />
            </v-fade-transition>
          </div>

          <div
            class="mhr-brands-body white elevation-4"
            :class="!isMobile && 'pos-absolute'"
          >
            <v-fade-transition mode="out-in" appear>
              <div
                v-for="({ brand_name, brand_description, hotels }) in [selBrand]"
                :key="brand_name"
                class="d-flex flex-column fill-height text-center text-lg-start"
              >
                <div class="mhr-text-h2 mhr-font mb-8 mb-lg-6" v-html="brand_name" />

                <div v-if="!isMobile" class="overflow-auto pe-2" v-html="brand_description" />
                <v-fade-transition
                  group mode="out-in"
                  class="d-flex mhr-brands-sub my-8 flex-shrink-1 justify-space-around"
                >
                  <v-img
                    v-for="({ hotel_logo }, h) in hotels" :key="h"
                    contain
                    class="flex-shrink-1 mx-3" style="width: 33%;"
                    :src="hotel_logo"
                  />
                </v-fade-transition>
                <div v-if="isMobile" class="overflow-auto pe-2" v-html="brand_description" />
                <v-spacer />
                <!-- <v-btn
                  text tile color="mhr-blue"
                  class="mhr-section-btn align-self-end mt-8 mt-lg-0 mt-auto"
                  :href="brand_url"
                  :to="!brand_url ? { name: 'All Brands'} : null"
                >
                  <div v-html="dataLabels.TEXT_DISCOVER" />
                  <v-img class="ms-2 flex-grow-0" src="/assets/icon/arrow-right.svg" />
                </v-btn> -->
              </div>
            </v-fade-transition>
          </div>

        </div>

      </div>

    </v-container>

    <div class="mhr-section mhr-blue pt-9 pt-lg-16">
      <v-container class="d-flex flex-column align-center">
        <div class="mhr-text-h2 mhr-font mhr-white--text mb-8 mb-lg-16" v-html="dataLabels.HOMEPAGE_ROOM_HEADLINE" />

        <swiper
          :slides-per-view="1"
          :breakpoints="{
            [breakpoint]: { slidesPerView: 3 }
          }"
          :pagination="isMobile"
          :navigation="!isMobile"
          :allow-touch-move="isMobile"
          :space-between="16"
          class="mhr-width-body pb-12 pb-lg-0"
          :class="isMobile && 'swiper-dark'"
          @swiper="$set(swipers, 'packages', $event)"
        >
          <swiper-slide v-for="({ offers_id, hotel_name, offers_title, offers_image, offers_preview, offers_url }) in dataOffers" :key="offers_id">
            <v-card tile color="mhr-white d-flex flex-column fill-height">
              <v-img class="mhr-packages-img" :src="offers_image" />
              <div class="px-5 py-4 mhr-packages-body d-flex flex-column align-lg-start fill-height">
                <div class="mhr-text-caption" v-html="hotel_name" />
                <div class="mhr-text-h3 mhr-font" v-html="offers_title" />
                <div class="mhr-text-body-2 mt-2 mb-4 flex-grow-1 overflow-auto pe-2" v-html="offers_preview" />
                <v-btn
                  text tile color="mhr-blue"
                  class="mhr-section-btn pa-0 mt-auto"
                  :to="{
                    name: 'Promotions Room Detail',
                    params: { promoId: offers_id, promoName: offers_url }
                  }"
                >
                  <div v-html="dataLabels.TEXT_LEARN_MORE" />
                  <v-img class="ms-2 flex-grow-0" :src="`${baseUrl}assets/icon/arrow-right.svg`" />
                </v-btn>
              </div>
            </v-card>
          </swiper-slide>
        </swiper>

      </v-container>
    </div>

    <div class="mhr-section">
      <v-container class="d-flex flex-column align-center">
        <div class="mhr-text-h2 mhr-font mb-8 mb-lg-16" v-html="dataLabels.HOMEPAGE_PRESS_HEADLINE" />

        <swiper
          :slides-per-view="1.125"
          :breakpoints="{
            [breakpoint]: { slidesPerView: 2 }
          }"
          :space-between="16"
          :pagination="isMobile"
          :navigation="!isMobile"
          :allow-touch-move="isMobile"
          class="pb-12 pb-lg-0"
          :class="isMobile ? 'overflow-visible' : 'swiper-dark'"
          @swiper="$set(swipers, 'updates', $event)"
        >
          <swiper-slide v-for="{ press_id, press_title, press_image, press_url, published_date } in dataPress" :key="press_id">
            <router-link
              :to="{
                name: 'Features Press Detail',
                params: { featureId: press_id, featureName: press_url }
              }"
            >
              <v-img
                class="mhr-updates-img mb-4 mb-lg-5"
                :src="press_image"
              />
              <div class="mhr-text-h4 mhr-font mhr-blue--text mb-4" v-html="press_title" />
              <div class="mhr-text-caption-2 mhr-black--text">{{ formatDate(published_date) }}</div>
            </router-link>
          </swiper-slide>
        </swiper>

      </v-container>
    </div>


  </div>
</template>

<script>
  // Extensions
  import View from '@/views/View'

  import { getHome } from '@/services/MHRService'

  export default {
    name: 'Home',
    extends: View,
    props: {
      id: {
        type: String,
        default: 'home'
      }
    },

    data () {
      return {
        ready: false,

        dataPage: {},
        dataHotels: [],
        dataBrands: [],
        dataOffers: [],
        dataPress: [],
        dataSocial: [],

        swipers: {},

        booking: {
          valid: null,
          periodModel: false,
          hotel: null,
          periodRange: [],
          promo: null
        },

        selBrandIndex: 0,

        rules: {
          required: [v => !!v?.length || v === 0 || !!v || 'Required.']
        }
      }
    },

    computed: {
      selBrand () { return this.dataBrands[this.selBrandIndex] },
      bookingLink () {
        // https://simplebooking.astonhotelsinternational.com/ibe/hotelbooking/search?hid=8268&lang=en#/q&guests=A,A&in=2022-10-07&out=2022-10-08
        const { hotel, promo } = this.booking
        const [start, end] = [...this.booking.periodRange].sort()
        const base = this.dataHotels[hotel]?.booknow_link

        return base && start && end
          ? this.toQueryUrl(base, {
            ...this.dataUtm,
            in: start,
            out: end,
            coupon: promo
          })
          : null
      }
    },

    mounted () {
      getHome().then(data => {
        this.ready = true

        this.dataPage = data.page
        this.dataHotels = data.search_hotels
        this.dataBrands = data.brands
        this.dataOffers = data.offers
        this.dataPress = data.press
        this.dataSocial = data.corp_socmed
      }).catch(err => console.error(err))
    }
  }
</script>
